<template>
  <transition name="fab-fade">
    <button
      v-if="isRepresentante && !anyModalOpen"
      type="button"
      class="rep-fab"
      data-tour-mobile="/ChamadaRepresentante /RegistrarPagamentoRepresentante /GerarPassagemRepresentante"
      title="Chamada, Registrar Pagamento e Passagem de Passageiro"
      @click="showMore = true"
    >
      <span class="material-symbols-outlined">apps</span>
    </button>
  </transition>

  <RepresentanteMoreSheet :show="showMore" @close="showMore = false" />
</template>

<script setup>
/**
 * RepresentanteFab.vue
 *
 * Botão flutuante GLOBAL (montado uma vez em App.vue, aparece em qualquer
 * tela) que dá acesso às 3 telas exclusivas do Representante (Chamada,
 * Registrar Pagamento, Passagem de Passageiro) — antes só existiam na
 * sidebar desktop, sem nenhum ponto de entrada no mobile.
 *
 * Não usa useCurrentUser() (cada chamada dele cria estado local isolado e
 * `init()` redireciona pra /login se não autenticado — errado pra um
 * componente global que também está montado nas telas de login/cadastro).
 * Escuta onAuthStateChanged direto do Firebase, então reage sozinho a
 * login/logout sem precisar de reload de página.
 */
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { onAuthStateChanged } from 'firebase/auth'
import { auth } from '@/firebase/firebaseConfig'
import { getUserData } from '@/services/userService'
import RepresentanteMoreSheet from '@/components/RepresentanteMoreSheet.vue'
import { anyModalOpen, useModalVisibility } from '@/composables/useModalVisibility'

const isRepresentante = ref(false)
const showMore = ref(false)
let removeListener = null

// O próprio painel que este botão abre também conta como "modal aberto" —
// some assim que o painel sobe, junto com qualquer outro modal da tela.
useModalVisibility(showMore)

async function checkRole(firebaseUser) {
  if (!firebaseUser) {
    isRepresentante.value = false
    return
  }
  try {
    const userData = await getUserData(firebaseUser)
    isRepresentante.value = (userData?.role || '').toLowerCase() === 'representante'
  } catch {
    isRepresentante.value = false
  }
}

onMounted(() => {
  removeListener = onAuthStateChanged(auth, checkRole)
})
onBeforeUnmount(() => removeListener && removeListener())
</script>

<style scoped>
.rep-fab {
  /* Só existe pra dar um ponto de entrada no mobile — no desktop a sidebar já
     mostra Chamada/Registrar Pagamento/Passagem de Passageiro direto, então
     o botão flutuante ali só ficava redundante por cima do conteúdo. */
  display: none;
  position: fixed;
  right: 1rem;
  bottom: calc(5.75rem + env(safe-area-inset-bottom, 0px));
  z-index: 250;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, var(--color-green-dark, #15803d), var(--color-green, #22c55e));
  color: #fff;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  cursor: pointer;
}
.rep-fab .material-symbols-outlined { font-size: 1.15rem; }

@media (max-width: 768px) {
  .rep-fab { display: flex; }
}

.fab-fade-enter-active,
.fab-fade-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.fab-fade-enter-from,
.fab-fade-leave-to { opacity: 0; transform: scale(0.8); }
</style>
